import type { CheckoutSurface } from '../types';

export type MobileStorePlatform = 'ios' | 'android';

/** True when checkout runs on a phone-sized touch device. */
export function isPhoneDevice(): boolean {
  return detectCheckoutSurface() === 'phone';
}

/** Best-effort store platform for contextual install links on phone. */
export function detectMobileStorePlatform(): MobileStorePlatform {
  if (typeof navigator === 'undefined') {
    return 'android';
  }

  const ua = navigator.userAgent ?? '';
  const isIos =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  return isIos ? 'ios' : 'android';
}

/** True when the browser reports an Android user agent. */
export function isAndroidDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /Android/i.test(navigator.userAgent ?? '');
}

export function detectCheckoutSurface(): CheckoutSurface {
  if (typeof window === 'undefined') {
    return 'desktop';
  }

  const narrow = window.matchMedia('(max-width: 520px)').matches;
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  return narrow && coarse ? 'phone' : 'desktop';
}

export function subscribeCheckoutSurface(
  onChange: (surface: CheckoutSurface) => void,
): () => void {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const mediaQueries = [
    window.matchMedia('(max-width: 520px)'),
    window.matchMedia('(pointer: coarse)'),
  ];

  const notify = (): void => {
    onChange(detectCheckoutSurface());
  };

  for (const query of mediaQueries) {
    query.addEventListener('change', notify);
  }
  window.addEventListener('orientationchange', notify);
  window.addEventListener('resize', notify);

  return () => {
    for (const query of mediaQueries) {
      query.removeEventListener('change', notify);
    }
    window.removeEventListener('orientationchange', notify);
    window.removeEventListener('resize', notify);
  };
}
